import { Play, Pause, RefreshCw, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useStartSync, usePauseSync, useResumeSync } from '@/api/partner-accounts';
import type { SyncStatus, ConnectionStatus } from '@/types/api';

interface SyncControlButtonsProps {
  accountId: string;
  syncStatus: SyncStatus;
  connectionStatus: ConnectionStatus;
  disabled?: boolean;
}

export function SyncControlButtons({
  accountId,
  syncStatus,
  connectionStatus,
  disabled = false,
}: SyncControlButtonsProps): JSX.Element {
  const { toast } = useToast();
  const startSync = useStartSync();
  const pauseSync = usePauseSync();
  const resumeSync = useResumeSync();

  const isDisconnected = connectionStatus !== 'connected';
  const isLoading = startSync.isPending || pauseSync.isPending || resumeSync.isPending;
  const isDisabled = disabled || isDisconnected;

  const handleStartSync = async () => {
    try {
      await startSync.mutateAsync(accountId);
      toast({
        title: 'Sync Started',
        description: 'The sync process has been initiated.',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to start sync';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handlePauseSync = async () => {
    try {
      await pauseSync.mutateAsync(accountId);
      // Different message depending on whether we're pausing an active sync or scheduled syncs
      const isActivelySyncing = syncStatus === 'syncing';
      toast({
        title: isActivelySyncing ? 'Sync Paused' : 'Scheduled Sync Paused',
        description: isActivelySyncing
          ? 'The sync process has been paused. Current batch will complete.'
          : 'Scheduled syncs have been paused. No automatic syncs will run until resumed.',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to pause sync';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleResumeSync = async () => {
    try {
      await resumeSync.mutateAsync(accountId);
      toast({
        title: 'Sync Resumed',
        description: 'Scheduled syncs have been resumed.',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resume sync';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  // Show Start Sync + Pause buttons for idle status
  if (syncStatus === 'idle') {
    return (
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={handleStartSync}
          disabled={isDisabled || isLoading}
          data-testid="start-sync-btn"
        >
          {startSync.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Play className="mr-2 h-4 w-4" />
          )}
          Start Sync
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handlePauseSync}
          disabled={isDisabled || isLoading}
          data-testid="pause-sync-btn"
        >
          {pauseSync.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Pause className="mr-2 h-4 w-4" />
          )}
          Pause Sync
        </Button>
      </div>
    );
  }

  // Show only Start Sync button for not_started status (no syncs scheduled yet)
  if (syncStatus === 'not_started') {
    return (
      <Button
        size="sm"
        onClick={handleStartSync}
        disabled={isDisabled || isLoading}
        data-testid="start-sync-btn"
      >
        {startSync.isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Play className="mr-2 h-4 w-4" />
        )}
        Start Sync
      </Button>
    );
  }

  // Show Pause button when syncing
  if (syncStatus === 'syncing') {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={handlePauseSync}
        disabled={isDisabled || isLoading}
        data-testid="pause-sync-btn"
      >
        {pauseSync.isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Pause className="mr-2 h-4 w-4" />
        )}
        Pause
      </Button>
    );
  }

  // Show Resume button when paused (only remaining status after above checks)
  return (
    <Button
      size="sm"
      onClick={handleResumeSync}
      disabled={isDisabled || isLoading}
      data-testid="resume-sync-btn"
    >
      {resumeSync.isPending ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <RefreshCw className="mr-2 h-4 w-4" />
      )}
      Resume
    </Button>
  );
}
